/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import { fragment } from 'ember-data-model-fragments/attributes';
import type BadgeAssertion from '@linc-technologies/linced-common/models/goals/badge-assertion';
import type Category from '@linc-technologies/linced-common/models/goals/category';
import type Subject from '@linc-technologies/linced-common/models/goals/subject';
import type Person from '@linc-technologies/linced-common/models/person';
import type School from '@linc-technologies/linced-common/models/school';
import moment from 'moment';

// Proto: goals.Badge.
export default class Badge extends Model {
  declare [Type]: 'goals/badge';

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  @computed('createTime')
  get createdDate() {
    return this.createTime
      ? moment.unix(this.createTime).format('MMMM Do YYYY')
      : '';
  }

  // schoolId is the school the badge relates to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  // campusId is the school campus the badge relates to.
  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  // assessorId is the person who awarded the badge.
  @belongsTo('person', { async: true, inverse: null })
  declare assessorId: AsyncBelongsTo<Person>;

  @alias('assessorId')
  declare assessor: AsyncBelongsTo<Person>;

  // personId is the person who received the badge.
  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  @alias('person.id')
  declare person_id: string | null | undefined;

  // subjectId is the subject the badge was awarded for.
  @belongsTo('goals/subject', { async: true, inverse: null })
  declare subjectId: AsyncBelongsTo<Subject>;

  @alias('subjectId')
  declare subject: AsyncBelongsTo<Subject>;

  @alias('subject.id')
  declare subject_id: string | null | undefined;

  // categoryId is the category the badge was awarded for.
  @belongsTo('goals/category', { async: true, inverse: null })
  declare categoryId: AsyncBelongsTo<Category>;

  @alias('categoryId')
  declare category: AsyncBelongsTo<Category>;

  @alias('category.id')
  declare category_id: string | null | undefined;

  // badgeAssertion is the Open Badges representation of the badge.
  @fragment('goals/badge-assertion')
  declare badgeAssertion: BadgeAssertion | null;

  @attr('string', { defaultValue: '' })
  declare mediaId: string;

  @alias('mediaId')
  declare badgeMedia: string | undefined;
}
